import { useState, KeyboardEvent, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
}

export const ChatInput = forwardRef<HTMLInputElement, ChatInputProps>(
  function ChatInput({ onSend, disabled }, ref) {
    const { t } = useTranslation('common')
    const [input, setInput] = useState('')

    const handleSend = () => {
      if (input.trim() && !disabled) {
        onSend(input)
        setInput('')
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    }

    const hasInput = input.trim().length > 0

    return (
      <div className="border-t border-gray-200 p-3 bg-white">
        <div className="flex gap-2 items-center">
          <input
            ref={ref}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatbot.placeholder')}
            disabled={disabled}
            className={cn(
              'flex-1 px-4 py-2.5 text-sm',
              'border border-gray-300 rounded-2xl',
              'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'placeholder:text-gray-400',
              'transition-all duration-200'
            )}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled || !hasInput}
            className={cn(
              'h-9 w-9 flex items-center justify-center flex-shrink-0 rounded-xl',
              'transition-all duration-200 ease-out',
              hasInput && !disabled
                ? 'bg-primary-600 text-white shadow-md shadow-primary-500/25 hover:bg-primary-700 hover:scale-105 active:scale-95'
                : 'bg-gray-100 text-gray-400',
              'disabled:cursor-not-allowed'
            )}
            aria-label={t('chatbot.send')}
          >
            <ArrowUp className="w-4 h-4 stroke-[2.5]" />
          </button>
        </div>
      </div>
    )
  }
)
